import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "How long does a project actually take?",
    a: "Fourteen calendar days from brief to launch. We run two projects per month, back to back. If we slip past day 14 for a reason that isn't yours, the last week is free.",
  },
  {
    q: "What's in scope, what isn't?",
    a: "In: strategy, art direction, copy editing, static front-end build, responsive design, SEO fundamentals, analytics, and a 30-minute handover. Out: multi-page CMS blogs, e-commerce checkouts, custom back-ends. We'll happily recommend partners for those.",
  },
  {
    q: "Do you actually hand-code everything?",
    a: "Yes. Semantic HTML, tokenised CSS, and a surgical layer of JavaScript. No Webflow, no page builders, no off-the-shelf themes. Average JS payload across our shipped sites is 22 kb.",
  },
  {
    q: "Can I keep working on it after launch?",
    a: "Of course — you own the repo, the domain, and the deploy pipeline. The code is deliberately written so your next engineer, or any solid freelancer, can pick it up in an afternoon.",
  },
  {
    q: "What does a project cost?",
    a: "A single-page site starts at $14k. Multi-page marketing sites and portfolios sit between $22k and $38k. 50% deposit, 50% on launch. No surprise invoices.",
  },
  {
    q: "What if I don't love the first style frame?",
    a: "We iterate on art direction once, free. If frame two doesn't earn a yes, you walk away with the brief, the frames, and your deposit back. We'd rather lose a project than ship a weak one.",
  },
];

const FaqSection = () => {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-container"
      className="relative py-32 sm:py-44"
    >
      <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20"
        >
          <div>
            <div className="mono-label mb-4">— 06 / FAQ</div>
            <h2 className="font-display font-light tracking-tight text-[clamp(2rem,4.4vw,4rem)] leading-[0.95] mb-6 max-w-[14ch]">
              Questions,
              <br />
              <span className="font-editorial italic text-accent">before they get awkward.</span>
            </h2>
            <p className="text-[14px] text-[#8A8D98] leading-relaxed max-w-[36ch]">
              Short, honest answers. If yours isn't here, write to{" "}
              <a
                href="mailto:studio@zcrazy.com"
                className="text-[#F4F4F2] underline underline-offset-4 decoration-accent/60 hover:decoration-accent"
              >
                studio@zcrazy.com
              </a>{" "}
              — we read everything.
            </p>
          </div>

          <div>
            {FAQS.map((item, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={item.q}
                  className={`border-t ${
                    i === FAQS.length - 1 ? "border-b" : ""
                  } border-white/8`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid="faq-item-trigger"
                    className="group w-full flex items-start justify-between gap-6 text-left py-6"
                    aria-expanded={isOpen}
                  >
                    <span
                      className={`font-display text-lg sm:text-xl font-medium tracking-tight transition-colors duration-300 ${
                        isOpen ? "text-[#F4F4F2]" : "text-[#C9CBD3] group-hover:text-[#F4F4F2]"
                      }`}
                    >
                      {item.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className={`h-9 w-9 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
                        isOpen
                          ? "border-accent/60 text-accent"
                          : "border-white/10 text-[#8A8D98] group-hover:border-accent/40"
                      }`}
                    >
                      <Plus size={16} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pb-6 pr-12 text-[15px] leading-relaxed text-[#8A8D98] max-w-[62ch]">
                          {item.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FaqSection;
